import { useState, useEffect, useRef } from "react";

/**
 * Combo Avocat recherche par code OU nom (autocomplete) — VERROUILLÉ À LA LISTE.
 * ---------------------------------------------------------------------------
 * Extrait de DecisionTab.jsx pour être réutilisé partout où un avocat doit
 * être sélectionné (Décision "Confié à" & "Signature", modal Partie Adverse,
 * Attestation, etc.). Comportement legacy strict :
 *   - Filtrage tolérant sur `code` ou `label` (case-insensitive).
 *   - Menu déroulant limité à 20 entrées max pour la perf.
 *   - Callback `onSelect(avocat)` reçoit l'objet complet — le parent est
 *     responsable de propager les champs qu'il souhaite.
 *   - Règle « liste seulement » : à la perte de focus, si le texte saisi ne
 *     correspond pas exactement (par code OU label) à un avocat de la liste,
 *     il est annulé (retour au nom validé). Champ vide → onClear().
 * ---------------------------------------------------------------------------
 */
const AvocatAutocomplete = ({ liste, code, nom, onSelect, onClear, testId, disabled = false, placeholder = "Rechercher…", listFontSize = 11 }) => {
  const [search, setSearch] = useState(nom || "");
  const [open, setOpen] = useState(false);
  const blurTimer = useRef(null);

  // Sync avec la prop nom quand elle change de l'extérieur
  // (chargement dossier existant, reset formulaire, etc.)
  useEffect(() => { setSearch(nom || ""); }, [nom]);
  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  const q = search.trim().toLowerCase();
  // Dédoublonnage par code : la table `avocats` peut contenir des doublons
  // (mêmes code/nom). Sans ça, React `key` entre en collision et laisse des
  // lignes obsolètes à l'écran au moment du filtrage (fantômes hors recherche).
  const seen = new Set();
  const source = (liste || []).filter(a => {
    const k = (a.code || "").toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const filtered = q
    ? source.filter(a =>
        (a.code || "").toLowerCase().includes(q)
        || (a.label || "").toLowerCase().includes(q))
    : source;

  // Validation à la perte de focus : verrou « liste seulement ».
  const handleBlur = () => {
    setOpen(false);
    const s = (search || "").trim();
    if (s === "") { if (onClear) onClear(); return; }   // champ vidé → clear
    const sl = s.toLowerCase();
    const exact = (liste || []).find(a =>
      (a.code || "").toLowerCase() === sl
      || (a.label || "").toLowerCase() === sl);
    if (exact) { onSelect(exact); setSearch(exact.label || exact.code); }
    else { setSearch(nom || ""); }                       // saisie non trouvée → annulée
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        className="win-input"
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => !disabled && setOpen(true)}
        onBlur={() => { blurTimer.current = setTimeout(handleBlur, 150); }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        data-testid={testId}
      />
      {open && !disabled && filtered.length > 0 && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, right: 0,
            background: "#fff", border: "1px solid #888", zIndex: 3000,
            maxHeight: 200, overflowY: "auto", fontSize: listFontSize,
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
          data-testid={`${testId}-menu`}
        >
          {filtered.slice(0, 20).map((a, i) => (
            <div
              key={`${a.code}-${i}`}
              onMouseDown={e => {
                e.preventDefault();
                onSelect(a);
                setSearch(a.label || a.code);
                setOpen(false);
              }}
              style={{ padding: "4px 8px", cursor: "pointer", borderBottom: "1px solid #eee" }}
              onMouseEnter={e => (e.currentTarget.style.background = "#dde7f5")}
              onMouseLeave={e => (e.currentTarget.style.background = "#fff")}
            >
              <strong>{a.code}</strong> — {a.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AvocatAutocomplete;
